import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { ChevronDown, Hourglass, Loader2, Sparkles } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { useUser } from '../../../contexts/UserContext';
import { cn } from '../../../lib/utils';
import { useForm } from '../../../hooks/useForm';
import {
  useFetchDashboardClaims,
  useUpdateClaim,
} from '../../../services/claims';
import Loading from '../../common/Loading';
import { useSaveUiState, useUiState } from '../../../services/uiState';
import HoverTip from '../../common/HoverTip';
import AssignToModal from '../../claim/AssignToModal';
import SubmitToVaModal from '../../claim/SubmitToVaModal';
import MeetingInviteModal from '../../calendar/components/MeetingInviteModal';

import ActivityBar from './components/ActivityBar';
import Toolbar from './components/Toolbar';
import SalesCard from './components/SalesCard';
import ClassicActivityBar from './classic/ClassicActivityBar';
import ClassicToolbar from './classic/ClassicToolbar';
import ClassicSalesCard from './classic/ClassicSalesCard';
import ClassicColumn from './classic/ClassicColumn';
import { availableTransitions } from '../../../lib/claimHelpers';
// os-scroll: the slim, arrowless scrollbar the boards use.
import '../../operations/onboardingSupport/onboardingSupport.css';

const VIEW_KEY = 'view_v2';

/*
'intake',
'awaiting medical records',
'in process',
'awaiting internal review',
'internal review approved',
'submitted to va',
'invoiced',
'payment plan',
'paid',
'dead',
*/
// Billing/admin + dead columns are hidden from providers. They're not provider
// workspaces. The server enforces the same restriction at the data-access
// layer (see `PROVIDER_RESTRICTED_STATUSES` in server/lib/providerClaimAccess.js),
// so a console-crafted API call can't bypass this UI hide. Keep these two
// lists in sync.
const PROVIDER_HIDDEN_COLUMNS = new Set([
  'invoiced',
  'payment plan',
  'paid',
  'dispute hold',
  'dead',
]);

// Intake and Awaiting Med. Recs. belong to Client Onboarding now (Nic
// 2026-10-09): the claim's status follows that board, and the kanban starts
// at In Process, where a Claims Manager takes over. They stay in COLUMNS for
// the status labels other pages read (VA Calls, VA Payments).
const ONBOARDING_COLUMNS = new Set(['intake', 'awaiting medical records']);

export const COLUMNS = [
  {
    id: 'intake',
    label: 'Intake',
  },
  {
    id: 'awaiting medical records',
    label: 'Awaiting Med. Recs.',
  },
  {
    id: 'in process',
    label: 'In Process',
  },
  {
    id: 'awaiting internal review',
    label: 'Awaiting Review',
  },
  {
    id: 'internal review approved',
    label: 'Review Approved',
  },
  {
    id: 'submitted to va',
    label: 'Submitted',
  },
  {
    id: 'invoiced',
    label: 'Invoiced',
    value: '_actualValue',
  },
  {
    id: 'payment plan',
    label: 'Payment Plan',
    value: '_actualValue',
  },
  {
    id: 'paid',
    label: 'Paid',
    value: '_actualValue',
  },
  {
    id: 'dispute hold',
    label: 'Dispute Hold',
  },
  {
    id: 'dead',
    label: 'Dead',
  },
];

// `allowedIds` is the set of column IDs this user is permitted to see (after
// userType + permission filtering). Stale entries in localStorage that point
// at no-longer-permitted IDs (e.g. a provider who used to see `paid`) get
// stripped on read so they can't surface back into the gear menu or the board.
//
// The companion "known columns" key records which column IDs the user's saved
// view has ever been offered: a column the user has NEVER seen (newly shipped,
// e.g. `dispute hold`) merges into their view automatically, while a column
// they deliberately hid (known but unchecked) stays hidden.
const KNOWN_COLUMNS_KEY = `${VIEW_KEY}:known`;

// The Claims Manager filter sticks (Nic 2026-10-20): saved to this browser
// for the first paint and to the person's account (ui-state
// kanbanCmFilter), so it survives reloads and signing back in anywhere.
// Per person (branch QA 2026-10-21): on a shared browser one person's choice
// must never seed another's account.
const cmFilterKey = (userId) => `kanban_cm_filter:${userId || 'anon'}`;
const isCmChoice = (v) =>
  !!v && typeof v === 'object' && typeof v.value === 'string' && typeof v.label === 'string';
const readCmFilter = (userId) => {
  try {
    const raw = window.localStorage.getItem(cmFilterKey(userId));
    const v = raw ? JSON.parse(raw) : null;
    return isCmChoice(v) ? { value: v.value, label: v.label } : null;
  } catch {
    return null;
  }
};
const writeCmFilter = (userId, v) => {
  try {
    if (v) window.localStorage.setItem(cmFilterKey(userId), JSON.stringify(v));
    else window.localStorage.removeItem(cmFilterKey(userId));
  } catch {
    /* storage blocked: the server copy still holds it */
  }
};
// Seed for saved views that predate the known-columns key: every column that
// existed before `dispute hold` shipped. Removable once localStorage has aged.
const LEGACY_KNOWN_COLUMN_IDS = COLUMNS.map((c) => c.id).filter(
  (id) => id !== 'dispute hold',
);

// Saved picks, plus any column they've never been offered (a newly shipped
// one turns on once); a column they hid stays hidden. COLUMNS order.
const mergeView = (allowedIds, savedView, knownIds) => {
  const savedSet = new Set(savedView.filter((id) => allowedIds.includes(id)));
  const known = new Set(knownIds);
  return allowedIds.filter((id) => savedSet.has(id) || !known.has(id));
};

const getDefaultView = (allowedIds) => {
  // Storage can be blocked, and a saved value can be junk (QA 2026-10-13):
  // either way the default columns show.
  let saved = null;
  try {
    const view = window.localStorage.getItem(VIEW_KEY);
    saved = view && /^\[/.test(view) ? JSON.parse(view) : null;
  } catch {
    saved = null;
  }
  const allowed = new Set(allowedIds);
  if (Array.isArray(saved)) {
    const savedSet = new Set(saved.filter((id) => allowed.has(id)));
    let known;
    try {
      const rawKnown = window.localStorage.getItem(KNOWN_COLUMNS_KEY);
      known = new Set(
        rawKnown && /^\[/.test(rawKnown)
          ? JSON.parse(rawKnown)
          : LEGACY_KNOWN_COLUMN_IDS,
      );
    } catch {
      known = new Set(LEGACY_KNOWN_COLUMN_IDS);
    }
    // Keep COLUMNS order: saved picks + never-before-seen columns.
    return allowedIds.filter((id) => savedSet.has(id) || !known.has(id));
  }
  return allowedIds;
};

// "Try the new look" (Nic 2026-10-24): where the Onboarding Workspace note
// was, for everyone, in both looks, so anyone can switch back and forth.
const LookSwitch = ({ on, onFlip }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={onFlip}
    // A touch of shine (Nic 2026-10-24: "premium"): a sparkle in the new
    // look's sky-to-violet, and the same gradient on the switch when it's on.
    // A drawn icon, not an emoji, so it looks the same on every computer.
    className="group/look inline-flex items-center gap-2 rounded-full border-0 bg-transparent p-0 text-[13px] font-semibold text-slate-600 outline-none hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:text-slate-300 dark:hover:text-white"
  >
    <Sparkles
      aria-hidden
      size={15}
      strokeWidth={2.25}
      className={cn(
        'shrink-0 transition-transform group-hover/look:rotate-12 group-hover/look:scale-110 motion-reduce:transition-none',
        on ? 'text-violet-500 dark:text-violet-300' : 'text-sky-500 dark:text-sky-300',
      )}
    />
    Try the new look
    <span
      aria-hidden
      className={cn(
        'relative inline-flex h-[18px] w-[32px] shrink-0 items-center rounded-full transition-colors',
        on
          ? 'bg-gradient-to-r from-sky-500 to-violet-500 shadow-[0_0_10px_rgba(139,92,246,0.35)]'
          : 'bg-slate-300 dark:!bg-white/20',
      )}
    >
      <span
        className={cn(
          'absolute left-[2px] h-[14px] w-[14px] rounded-full bg-white shadow-sm transition-transform',
          on && 'translate-x-[14px]',
        )}
      />
    </span>
  </button>
);

// The In Process hourglass: just the icon, grey off, amber on. It filters In
// Process to the claims its CMs marked waiting on documents, and back.
const WaitingFilter = ({ on, waiting, onFlip }) => (
  <HoverTip
    text={
      on
        ? 'Showing only claims waiting on documents. Click to show every claim.'
        : `${waiting} waiting on documents. Click to show only those.`
    }
  >
    <button
      type="button"
      aria-pressed={on}
      aria-label={on ? 'Show every claim' : 'Show only claims waiting on documents'}
      className={cn(
        'inline-flex items-center border-0 bg-transparent p-0.5 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50 rounded',
        on
          ? 'text-amber-500 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-300'
          : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300',
      )}
      onClick={onFlip}
    >
      <Hourglass size={14} strokeWidth={on ? 2.5 : 2} />
    </button>
  </HoverTip>
);

// look and onLookChange come from Dashboard (useDashboardLook), which also
// picks the page width: classic sits in the normal page, the new look fills
// the window.
const SalesDashboard = ({ look = 'new', onLookChange = () => {} }) => {
  const { hasPermission, hasRole, permissions, user } = useUser();
  // Mirrors the backend's isProviderContext check in claimsController.js so
  // a user whose `userType` is something else but who carries the provider
  // role still gets the provider-restricted view.
  const isProvider = user?.userType === 'provider' || hasRole('provider');
  const cols = useMemo(
    () =>
      COLUMNS.filter((c) => {
        if (ONBOARDING_COLUMNS.has(c.id)) return false;
        if (isProvider && PROVIDER_HIDDEN_COLUMNS.has(c.id)) return false;
        return true;
      }),
    [isProvider],
  );
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [view, setView] = useState(() => getDefaultView(cols.map((c) => c.id)));
  const [items, setItems] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [columnPages, setColumnPages] = useState({}); // Track page per status
  const [columnLoadingMore, setColumnLoadingMore] = useState({}); // Track loading state per status
  const [assignTo, setAssignTo] = useState(null);
  const [submitToVa, setSubmitToVa] = useState(null);
  const [meetingInvite, setMeetingInvite] = useState(null);
  // While a card is in the air, other cards' tooltips stay quiet.
  const [dragging, setDragging] = useState(false);

  // Only people who see every claim get the filter, so only they restore it.
  const [initialCm] = useState(() =>
    hasPermission('claims:view_all') ? readCmFilter(user?.userId) : null,
  );
  const classic = look === 'classic';
  const { form, onChange } = useForm({
    search: '',
    status: 'active',
    user: initialCm,
  });
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search: undefined,
    status: 'active',
    userId: undefined,
    statuses: view.join(','),
    claimAssignedToUserId: initialCm ? initialCm.value : undefined,
    // The classic tile's task checklist; the new look never asks for it.
    withTasks: look === 'classic' ? 1 : undefined,
  });
  const queryClient = useQueryClient();
  // Use permission-based check instead of adminRole
  const canViewAllClaims = hasPermission('claims:view_all');
  // View only past onboarding (Nic 2026-10-09): every column here is past
  // onboarding, so Sales Reps and Onboarding Specialists look but don't
  // drag, assign or delete. The server enforces it (claimEditGuard).
  const canChangeClaims =
    hasPermission('billing:manage') ||
    (hasPermission('claims:edit') && hasPermission('claims:edit_after_onboarding'));
  const searchTimeoutRef = useRef(null);

  const { isLoading, data } = useFetchDashboardClaims(filters);

  // Column choices are saved on the server too (Nic 2026-10-09, migration
  // 210), so they follow the person; this browser's copy is just the first
  // paint. Once the server answers, its choice wins; with none saved yet,
  // this browser's choice is sent up.
  const ui = useUiState();
  const saveUiState = useSaveUiState();
  const serverViewReady = useRef(false);
  useEffect(() => {
    if (serverViewReady.current || ui.isLoading || ui.isError) return;
    serverViewReady.current = true;
    const saved = ui.data?.kanbanColumns;
    const allowedIds = cols.map((c) => c.id);
    if (saved) {
      const next = mergeView(allowedIds, saved.view, saved.known);
      if (next.join(',') !== view.join(',')) setView(next);
    } else {
      saveUiState('kanbanColumns', { view, known: allowedIds });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.isLoading, ui.isError]);

  // The classic tile's task checklist rides along only in the classic look.
  useEffect(() => {
    setFilters((f) => (f.withTasks === (classic ? 1 : undefined) ? f : { ...f, withTasks: classic ? 1 : undefined }));
  }, [classic]);

  // The board is its own scroll area filling the rest of the window, so its
  // sideways scrollbar sits at the bottom of the screen, not under the
  // longest column (Nic 2026-10-09).
  const boardRef = useRef(null);
  const [boardHeight, setBoardHeight] = useState(null);
  useLayoutEffect(() => {
    // The classic look scrolls the page like it always did.
    if (classic) {
      setBoardHeight(null);
      return undefined;
    }
    const fit = () => {
      const el = boardRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      const room = window.innerHeight - top - 12;
      setBoardHeight(Math.max(360, room));
      // When the board fits, the page itself never scrolls: a tooltip is
      // briefly added at the end of the page while it's placed, and that
      // flashed the page scrollbar in and out (the board "shook"). A window
      // too short for the board keeps page scrolling.
      document.documentElement.style.overflowY = room >= 360 ? 'hidden' : '';
    };
    fit();
    window.addEventListener('resize', fit);
    return () => {
      window.removeEventListener('resize', fit);
      document.documentElement.style.overflowY = '';
    };
  }, [isLoading, data, classic]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setItems([...data.rows]);
    setSummaries(data.summaries || []);
    // Reset load more states on fresh data
    setColumnPages({});
    setColumnLoadingMore({});
  }, [data]);

  useEffect(() => {
    window.localStorage.setItem(VIEW_KEY, JSON.stringify(view));
    // Record every column the user has now been offered, so future new
    // columns auto-merge while deliberate hides stay hidden.
    window.localStorage.setItem(
      KNOWN_COLUMNS_KEY,
      JSON.stringify(cols.map((c) => c.id)),
    );
    if (serverViewReady.current) {
      saveUiState('kanbanColumns', { view, known: cols.map((c) => c.id) });
    }
    setFilters({ ...filters, statuses: view.join(',') });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const updateClaim = useUpdateClaim(
    () => {
      toast.success('Claim status was successfully updated.');
      queryClient.invalidateQueries('claims');
    },
    { onError: (err) => toast.error(err.message) },
  );

  const onSubmit = (e) => {
    e.preventDefault();

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search by 400ms
    searchTimeoutRef.current = setTimeout(() => {
      setFilters({
        ...filters,
        search: form.search,
        status: form.status,
        claimAssignedToUserId: form.user ? form.user.value : undefined,
        page: 1,
      });
      // Reset pagination on search
      setColumnPages({});
      setColumnLoadingMore({});
    }, 400);
  };

  const handleLoadMore = async (status) => {
    const nextPage = (columnPages[status] || 1) + 1;
    setColumnLoadingMore({ ...columnLoadingMore, [status]: true });

    try {
      const res = await axios.get(`/dashboard/claims`, {
        params: {
          ...filters,
          statuses: status,
          page: nextPage,
        },
      });

      if (res.data && res.data.rows) {
        // Append new rows to items
        setItems([...items, ...res.data.rows]);
        // Update page tracker for this status
        setColumnPages({ ...columnPages, [status]: nextPage });
      }
    } catch (err) {
      toast.error('Failed to load more claims');
      console.error('Load more error:', err);
    } finally {
      setColumnLoadingMore({ ...columnLoadingMore, [status]: false });
    }
  };

  // The saved Claims Manager filter: once the server answers, its choice
  // wins (another computer may have changed it); a change is saved both ways.
  const serverCmReady = useRef(false);
  useEffect(() => {
    if (serverCmReady.current || ui.isLoading || ui.isError) return;
    serverCmReady.current = true;
    if (!canViewAllClaims) return;
    const has = ui.data && Object.prototype.hasOwnProperty.call(ui.data, 'kanbanCmFilter');
    if (!has) {
      if (form.user) saveUiState('kanbanCmFilter', { value: form.user.value, label: form.user.label });
      return;
    }
    const saved = isCmChoice(ui.data.kanbanCmFilter) ? ui.data.kanbanCmFilter : null;
    if ((saved?.value || null) !== (form.user?.value || null)) {
      onChange({ target: { name: 'user', value: saved ? { value: saved.value, label: saved.label } : null } });
      // The fetch filter too: the form.user effect below only reacts once
      // the board has loaded.
      setFilters((f) => ({ ...f, claimAssignedToUserId: saved ? saved.value : undefined, page: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.isLoading, ui.isError]);
  useEffect(() => {
    if (!canViewAllClaims) return;
    const v = form.user ? { value: form.user.value, label: form.user.label } : null;
    writeCmFilter(user?.userId, v);
    if (serverCmReady.current) saveUiState('kanbanCmFilter', v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.user]);

  // Handle user filter change separately (no debounce needed for dropdown)
  useEffect(() => {
    if (data) {
      setFilters({
        ...filters,
        claimAssignedToUserId: form.user ? form.user.value : undefined,
        page: 1,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.user]);

  const onDragEnd = (result) => {
    setDragging(false);
    const newItems = [...items];

    if (!result.destination || !result.destination.droppableId) {
      return;
    }

    if (result.source.droppableId === result.destination.droppableId) {
      return;
    }

    // Block back-to-back drops while a previous mutation is still in flight.
    // Without this, the next drag captures `prevStatus` from already-
    // optimistically-mutated state, and the rollback (if the in-flight
    // mutation errors) restores to the wrong column.
    if (updateClaim.isLoading) {
      toast.info('Hang on — finishing the previous status change.');
      return;
    }

    // update the status of the record in question
    const item = newItems.find((i) => i.claimId === result.draggableId);
    if (!item) {
      return;
    }

    // ENFORCE: Only allow status change if allowed by availableTransitions
    const allowedStatuses = availableTransitions(item.claimStatus, permissions);
    if (!allowedStatuses.includes(result.destination.droppableId)) {
      toast.error('You are not permitted to move this claim to that status.');
      return;
    }

    // Require submission type + proof before persisting transition into "submitted to va"
    if (
      result.destination.droppableId === 'submitted to va' &&
      item.claimStatus !== 'submitted to va'
    ) {
      setSubmitToVa({ claim: item, extraPayload: {} });
      return;
    }

    // Require claim manager assignment before persisting transition into "in process"
    // (admin override may bypass).
    if (
      result.destination.droppableId === 'in process' &&
      item.claimStatus !== 'in process' &&
      !item.claimAssignedToUserId &&
      !item.assignedToUserId
    ) {
      setAssignTo({
        claimId: item.claimId,
        userId: item.userId,
        adminId: item.claimAssignedToUserId || item.assignedToUserId,
        adminFirstName: item.assignedToUserFirstName,
        adminLastName: item.assignedToUserLastName,
        _postAssignStatus: 'in process',
      });
      return;
    }

    // Placing a claim on Dispute Hold requires a note (the paper trail for
    // disputes reported directly to Finance). The API rejects the move
    // without one.
    const extraPayload = {};
    if (
      result.destination.droppableId === 'dispute hold' &&
      item.claimStatus !== 'dispute hold'
    ) {
      const note = window.prompt(
        'Dispute Hold freezes all billing for this claim: no invoice emails, no reminders, no payment plan charges, no automated billing.\n\nEnter a note about the dispute (required):',
      );
      if (note === null) return;
      if (!note.trim()) {
        toast.error('A dispute note is required to place a claim on Dispute Hold.');
        return;
      }
      extraPayload.disputeHoldNote = note.trim();
    }

    const prevStatus = item.claimStatus;
    const prevUpdatedOn = item.claimUpdatedOn;
    const nextStatus = result.destination.droppableId;

    item.claimStatus = nextStatus;
    item.claimUpdatedOn = moment.utc().format();

    updateClaim.mutate(
      {
        claimId: item.claimId,
        payload: { claimStatus: nextStatus, ...extraPayload },
      },
      {
        // Roll the tile back to its source column when the backend rejects
        // the move (e.g. the address gate). The hook-level onError already
        // shows the toast — this per-call hook just reverts the optimistic
        // state so the tile doesn't appear stuck in the new column.
        onError: () => {
          setItems((current) =>
            current.map((c) =>
              c.claimId === item.claimId
                ? { ...c, claimStatus: prevStatus, claimUpdatedOn: prevUpdatedOn }
                : c,
            ),
          );
        },
      },
    );

    setItems([...newItems]);
  };

  const onBeforeCaptureResponder = (before) => {
    const claim = items.find((r) => r.claimId === before.draggableId);

    if (!claim) {
      toast.error(
        'It looks like we encountered an error with this object. Please refresh the page to try and resolve the problem.',
      );
      return;
    }

    const canMoveTo = availableTransitions(claim.claimStatus, permissions);

    setAvailableStatuses([...canMoveTo, claim.claimStatus]);
    setDragging(true);
  };

  // The tile has no "..." menu (Nic 2026-10-22): a click opens the claim,
  // and everything else is done inside the claim. The one action left is
  // the meeting invite, its own button on the tile, for the same people who
  // had the old menu (never providers).
  const openMeetingInvite = useMemo(() => {
    if (hasRole('provider') || !canChangeClaims) return null;
    return (row) =>
      setMeetingInvite({
        userId: {
          label: `${row.userFirstName} ${row.userLastName}`,
          value: row.userId,
          record: { userEmail: row.userEmail || '' },
        },
      });
  }, [hasRole, canChangeClaims]);
  // The classic tile keeps its "..." menu with just that one action (Nic
  // 2026-10-24); no menu at all for anyone who can't send it.
  const classicActions = useMemo(
    () => (openMeetingInvite ? [{ label: 'Create Meeting Invite', onClick: (row) => openMeetingInvite(row) }] : null),
    [openMeetingInvite],
  );

  // Memoize column data calculations to avoid recalculating on every render
  const columnData = useMemo(() => {
    const result = {};

    cols.forEach((c) => {
      // Get summary data for this status
      const summary = summaries.find((s) => s.status === c.id) || {
        status: c.id,
        count: 0,
        min: 0,
        max: 0,
      };

      // Count loaded items for this status
      const loadedCount = items.filter((i) => i.claimStatus === c.id).length;
      const hasMore = loadedCount < summary.count;

      const cards = items
        .filter((i) => i.claimStatus === c.id)
        .map((item, itemIdx) => {
          return (
            <Draggable
              key={item.claimId}
              draggableId={item.claimId}
              index={itemIdx}
              isDragDisabled={!canChangeClaims}
            >
              {(provided, snapshot) =>
                classic ? (
                  <div ref={provided.innerRef} {...provided.draggableProps}>
                    <ClassicSalesCard
                      card={item}
                      handleProps={provided.dragHandleProps}
                      className={snapshot.isDragging ? 'dragging' : ''}
                      actions={classicActions}
                      canViewAllClaims={canViewAllClaims}
                    />
                  </div>
                ) : (
                <div ref={provided.innerRef} {...provided.draggableProps} className="pb-2.5">
                  <SalesCard
                    card={item}
                    handleProps={provided.dragHandleProps}
                    className={snapshot.isDragging ? 'dragging' : ''}
                    onMeetingInvite={openMeetingInvite}
                    canViewAllClaims={canViewAllClaims}
                    canDrag={canChangeClaims}
                    quiet={dragging}
                  />
                </div>
                )
              }
            </Draggable>
          );
        });

      result[c.id] = {
        cards,
        total: summary.count,
        waiting: summary.waiting || 0,
        hasMore,
      };
    });

    return result;
  }, [cols, items, summaries, openMeetingInvite, classicActions, classic, canViewAllClaims, canChangeClaims, dragging]);

  // Waiting on documents (Nic 2026-10-09): In Process filters to the claims
  // its CMs marked, and back. Both looks.
  const toggleWaiting = (status, on) => {
    setFilters({ ...filters, waitingOnly: on ? undefined : status, page: 1 });
    setColumnPages({});
    setColumnLoadingMore({});
  };

  // A hello at the top (Nic 2026-10-09), by the viewer's own clock.
  const hour = new Date().getHours();
  const partOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  const firstName = user?.userPreferredName || user?.userFirstName || '';

  return (
    <div>
      <div className="d-flex flex-wrap align-items-baseline justify-content-between gap-2 mb-3">
        <h4 className="mb-0 fw-bold">
          Good {partOfDay}
          {firstName ? `, ${firstName}` : ''}
        </h4>
        <LookSwitch on={!classic} onFlip={() => onLookChange(classic ? 'new' : 'classic')} />
      </div>
      {classic ? (
        <>
          <ClassicActivityBar />
          <ClassicToolbar
            data={items}
            total={data ? data.total : 0}
            totalMin={data ? data.totalMin : 0}
            totalMax={data ? data.totalMax : 0}
            form={form}
            onChange={onChange}
            onSubmit={onSubmit}
            columns={cols}
            view={view}
            onViewChange={(newView) => setView(newView)}
            canViewAllClaims={canViewAllClaims}
          />
          <DragDropContext onDragEnd={onDragEnd} onBeforeCapture={onBeforeCaptureResponder}>
            <div className="d-flex justify-content-between align-items-start gap-1 overflow-x-auto">
              {cols
                .filter((c) => view.includes(c.id))
                .map((c) => {
                  const { cards, total, waiting, hasMore } = columnData[c.id] || {
                    cards: [],
                    total: 0,
                    waiting: 0,
                    hasMore: false,
                  };
                  const waitingOn = filters.waitingOnly === c.id;
                  const showWaiting = c.id === 'in process' && (waiting > 0 || waitingOn);
                  const isAvailToMOveTo = availableStatuses.includes(c.id);
                  const isLoadingMore = columnLoadingMore[c.id] || false;
                  return (
                    <div key={c.id} style={{ flex: 1 }}>
                      <ClassicColumn>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-1">
                            <h5 className="mb-0">{c.label}</h5>
                            {showWaiting ? (
                              <WaitingFilter
                                on={waitingOn}
                                waiting={waiting}
                                onFlip={() => toggleWaiting(c.id, waitingOn)}
                              />
                            ) : null}
                          </div>
                          <p className="mb-0">({total})</p>
                        </div>
                        <Droppable droppableId={c.id} isDropDisabled={!isAvailToMOveTo}>
                          {(provided, snapshot) => (
                            <div
                              className={snapshot.isDraggingOver ? 'dragging-over' : ''}
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              style={{ minHeight: cards.length === 0 ? '50px' : 'auto' }}
                            >
                              {cards}
                              {provided.placeholder}
                              {hasMore && (
                                <div style={{ padding: '0.5rem', textAlign: 'center' }}>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleLoadMore(c.id)}
                                    disabled={isLoadingMore}
                                  >
                                    {isLoadingMore ? 'Loading...' : 'Load More'}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </Droppable>
                      </ClassicColumn>
                    </div>
                  );
                })}
            </div>
          </DragDropContext>
        </>
      ) : (
      <>
      <Toolbar
        data={items}
        total={data ? data.total : 0}
        totalMin={data ? data.totalMin : 0}
        totalMax={data ? data.totalMax : 0}
        form={form}
        onChange={onChange}
        onSubmit={onSubmit}
        columns={cols}
        view={view}
        onViewChange={(newView) => setView(newView)}
        // The CM filter lists staff (GET /users needs users:view), so Sales
        // Reps, who see every claim but no staff list, don't get it (QA
        // 2026-10-13: it was a Permission denied toast).
        canViewAllClaims={canViewAllClaims && hasPermission('users:view')}
        quickPanels={<ActivityBar />}
      />
      <DragDropContext
        onDragEnd={onDragEnd}
        onBeforeCapture={onBeforeCaptureResponder}
      >
        {/* Open columns, no boxed lanes (Nic 2026-10-09: "more premium"):
            a header with a count, then the cards. */}
        <div
          ref={boardRef}
          className="os-scroll flex items-start gap-4 overflow-auto px-1.5 pb-2"
          style={boardHeight ? { height: boardHeight } : undefined}
        >
          {cols
            .filter((c) => view.includes(c.id))
            .map((c) => {
              const { cards, total, waiting, hasMore } = columnData[c.id] || {
                cards: [],
                total: 0,
                waiting: 0,
                hasMore: false,
              };
              // Waiting on documents (Nic 2026-10-09): In Process filters
              // to the claims its CMs marked, and back.
              const waitingOn = filters.waitingOnly === c.id;
              const showWaiting = c.id === 'in process' && (waiting > 0 || waitingOn);

              const isAvailToMOveTo = availableStatuses.includes(c.id);
              const isLoadingMore = columnLoadingMore[c.id] || false;

              return (
                <section key={c.id} className="flex min-w-[240px] flex-1 flex-col">
                  <header className="flex min-h-[30px] items-center gap-2 px-1 pb-2">
                    <h3 className="mb-0 truncate text-[13px] font-bold uppercase tracking-[0.06em] text-slate-600 dark:text-slate-300">
                      {c.label}
                    </h3>
                    {/* Waiting on documents (Nic 2026-10-09): just the icon, right
                        after the heading (before the count, so a changing count
                        never shifts it). Grey off, amber on. It filters In
                        Process to the claims its CMs marked, and back. */}
                    {showWaiting ? (
                      <WaitingFilter on={waitingOn} waiting={waiting} onFlip={() => toggleWaiting(c.id, waitingOn)} />
                    ) : null}
                    <span className="rounded-full bg-slate-900/[0.06] px-2 text-[11px] font-semibold tabular-nums text-slate-500 dark:!bg-white/10 dark:text-slate-400">
                      {total}
                    </span>
                  </header>
                  <Droppable droppableId={c.id} isDropDisabled={!isAvailToMOveTo}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={cn(
                          '-mx-1.5 min-h-[72px] rounded-2xl px-1.5 pt-0.5 transition-[background-color,box-shadow]',
                          snapshot.isDraggingOver &&
                            'bg-sky-50/80 ring-1 ring-sky-200 dark:!bg-sky-500/[0.07] dark:ring-sky-400/30',
                        )}
                      >
                        {cards.length === 0 && !snapshot.isDraggingOver ? (
                          <div className="mb-2.5 rounded-2xl border-[1px] border-dashed border-slate-300/80 px-3 py-6 text-center text-xs text-slate-400 dark:border-white/10">
                            Nothing here
                          </div>
                        ) : null}
                        {cards}
                        {provided.placeholder}
                        {hasMore ? (
                          <button
                            type="button"
                            onClick={() => handleLoadMore(c.id)}
                            disabled={isLoadingMore}
                            className="group/more mb-4 mt-2 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-400 bg-transparent py-2 text-[12px] font-semibold text-slate-500 outline-none transition-[background-color,border-color,color] hover:border-sky-400 hover:bg-sky-50/70 hover:text-sky-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50 disabled:cursor-wait disabled:opacity-70 dark:border-slate-500 dark:text-slate-400 dark:hover:border-sky-400/60 dark:hover:!bg-sky-500/[0.08] dark:hover:text-sky-300"
                          >
                            {isLoadingMore ? (
                              <>
                                <Loader2 size={13} className="animate-spin" /> Loading
                              </>
                            ) : (
                              <>
                                <ChevronDown size={14} className="transition-transform group-hover/more:translate-y-px" />
                                Show more
                                <span className="rounded-full bg-slate-900/[0.06] px-1.5 text-[11px] tabular-nums text-slate-500 group-hover/more:bg-sky-500/15 group-hover/more:text-sky-700 dark:!bg-white/10 dark:text-slate-400 dark:group-hover/more:text-sky-300">
                                  {total - cards.length}
                                </span>
                              </>
                            )}
                          </button>
                        ) : null}
                      </div>
                    )}
                  </Droppable>
                </section>
              );
            })}
        </div>
      </DragDropContext>
      </>
      )}
      {isLoading ? <Loading /> : null}
      <AssignToModal
        show={assignTo !== null}
        toggle={() => setAssignTo(null)}
        claim={assignTo}
        allowContinueUnassigned={Boolean(assignTo && assignTo._postAssignStatus)}
        onSuccess={() => {
          queryClient.invalidateQueries('claims');
          queryClient.invalidateQueries({ queryKey: ['claim'] });

          if (assignTo && assignTo._postAssignStatus) {
            updateClaim.mutate(
              {
                claimId: assignTo.claimId,
                payload: { claimStatus: assignTo._postAssignStatus },
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({ queryKey: ['claim'] });
                },
              },
            );
          }
        }}
      />
      <SubmitToVaModal
        show={submitToVa !== null}
        toggle={() => setSubmitToVa(null)}
        claim={submitToVa ? submitToVa.claim : null}
        submitting={updateClaim.isLoading}
        onConfirm={async (submissionEvent) => {
          const c = submitToVa ? submitToVa.claim : null;
          if (!c) return;

          updateClaim.mutate(
            {
              claimId: c.claimId,
              payload: {
                claimStatus: 'submitted to va',
                submissionEvent,
                ...(submitToVa.extraPayload || {}),
              },
            },
            {
              onSuccess: () => {
                setSubmitToVa(null);
                queryClient.invalidateQueries('claims');
              },
            },
          );
        }}
      />
      <MeetingInviteModal
        show={meetingInvite !== null}
        toggle={() => setMeetingInvite(null)}
        onSuccess={() => {
          queryClient.invalidateQueries('calendar');
        }}
        meetingInvite={meetingInvite}
      />
    </div>
  );
};

export default SalesDashboard;
